import type { KeycloakConfig, KeycloakInitOptions } from 'keycloak-js'
import type { ObjectPlugin } from 'vue'
import { getToken, useKeycloak, vueKeycloak } from '@josempgon/vue-keycloak'

/** Le jeton est renouvelé s'il lui reste moins que cette durée de validité. */
const TOKEN_MIN_VALIDITY_SECONDS = 120
/** Renouvellement proactif : garde la session SSO vivante tant qu'un onglet est ouvert. */
const REFRESH_INTERVAL_MS = 30_000
/** Marge plus courte utilisée par les intercepteurs HTTP. */
const REQUEST_MIN_VALIDITY_SECONDS = 30

const defaultConfig: KeycloakConfig = {
  url: import.meta.env.VITE_KEYCLOAK_URL,
  realm: 'omnimetrie',
  clientId: 'prophet-portal',
}

/** Pages volontairement consultables sans compte (affichages publics / bornes). */
const PUBLIC_PATH_PREFIXES = ['/ott']

function estPagePublique(pathname = window.location.pathname) {
  return PUBLIC_PATH_PREFIXES.some((prefix) => pathname === prefix || pathname.startsWith(prefix + '/'))
}

const defaultInitOptions: KeycloakInitOptions = {
  flow: 'standard',
  checkLoginIframe: false,
  // `login-required` authentifie dès le chargement : si la session SSO est encore ouverte,
  // le retour est immédiat et transparent, sinon Keycloak affiche son formulaire.
  onLoad: estPagePublique() ? 'check-sso' : 'login-required',
  enableLogging: import.meta.env.DEV,
}

let resolveReady!: () => void
/**
 * Résolue une fois `keycloak.init()` terminé (authentifié ou non, succès ou échec).
 * À attendre avant toute décision basée sur `isAuthenticated`.
 */
export const keycloakReady = new Promise<void>((resolve) => {
  resolveReady = resolve
})

let refreshInFlight: Promise<boolean> | null = null
let redirecting = false

/** Redirige vers Keycloak en conservant l'URL courante comme destination de retour. */
export function redirectToKeycloakLogin(returnUrl: string = window.location.href) {
  if (redirecting) return
  redirecting = true
  const { keycloak } = useKeycloak()
  void keycloak.value?.login({ redirectUri: returnUrl })
}

/**
 * Renouvelle le jeton s'il est proche de l'expiration.
 * Retourne `false` si la session ne peut plus être prolongée (refresh token expiré).
 */
export async function ensureFreshToken(minValidity = TOKEN_MIN_VALIDITY_SECONDS): Promise<boolean> {
  const { keycloak } = useKeycloak()
  if (!keycloak.value?.authenticated) return false

  refreshInFlight ??= (async () => {
    try {
      await getToken(minValidity)
      return true
    } catch {
      return false
    } finally {
      refreshInFlight = null
    }
  })()

  return refreshInFlight
}

/** Renouvellement automatique ; renvoie vers Keycloak si la session est réellement perdue. */
async function refreshOrRelogin(minValidity = TOKEN_MIN_VALIDITY_SECONDS) {
  // Hors ligne, un échec ne veut pas dire que la session est expirée : ne pas rediriger.
  if (!navigator.onLine) return
  const refreshed = await ensureFreshToken(minValidity)
  if (!refreshed) redirectToKeycloakLogin()
}

/**
 * Jeton valide pour les intercepteurs HTTP.
 * Retourne `undefined` au lieu de lever si la session est perdue : une redirection est déclenchée.
 */
export async function getAccessToken(): Promise<string | undefined> {
  const { keycloak } = useKeycloak()
  if (!keycloak.value?.authenticated) return undefined

  const refreshed = await ensureFreshToken(REQUEST_MIN_VALIDITY_SECONDS)
  if (!refreshed) {
    redirectToKeycloakLogin()
    return undefined
  }
  return keycloak.value.token
}

function startTokenRefresh() {
  const { keycloak } = useKeycloak()
  const kc = keycloak.value
  if (!kc) return

  kc.onTokenExpired = () => void refreshOrRelogin()
  kc.onAuthRefreshError = () => redirectToKeycloakLogin()

  window.setInterval(() => void refreshOrRelogin(), REFRESH_INTERVAL_MS)

  // Après une veille prolongée ou un retour sur l'onglet, le jeton est souvent périmé.
  const onWakeUp = () => {
    if (document.visibilityState !== 'visible') return
    void refreshOrRelogin()
  }
  document.addEventListener('visibilitychange', onWakeUp)
  window.addEventListener('pageshow', onWakeUp)
  window.addEventListener('online', onWakeUp)
}

export default {
  async install(app, options) {
    try {
      await vueKeycloak.install(app, options ?? {
        config: defaultConfig,
        initOptions: defaultInitOptions,
      })
      startTokenRefresh()
    } catch (error) {
      console.error('Keycloak init failed', error)
    } finally {
      resolveReady()
    }
  },
} satisfies ObjectPlugin
